import * as Comparators from "portal/utils/comparators";
import { createSlice, original, type PayloadAction } from "@reduxjs/toolkit";
import { LocationHistoryRecord } from "protos/rtc/location_history";

// Records collect in `recent` until it holds this many, then move onto
// `historical`, so map layers re-upload the large buffer only on rollover.
const RECENT_LIMIT = 60;

// A robot's location history, sorted by timestamp. `historical` holds
// everything before `recent`.
export interface TractorHistory {
  historical: LocationHistoryRecord[];
  recent: LocationHistoryRecord[];
}

export const EMPTY_HISTORY: TractorHistory = { historical: [], recent: [] };

export const allRecords = (
  history: TractorHistory | undefined
): LocationHistoryRecord[] | undefined =>
  history && [...history.historical, ...history.recent];

export const latestRecord = ({
  historical,
  recent,
}: TractorHistory): LocationHistoryRecord | undefined =>
  recent.at(-1) ?? historical.at(-1);

interface State {
  // Map from robot serial to a consecutive subset of its location history.
  // The history for any robot should be internally complete: e.g., if the
  // history contains data from Tuesday and from Thursday, then it should also
  // contain any relevant data from Wednesday. Data from Monday or Friday might
  // not be included if it hasn't been fetched.
  history: Record<string, TractorHistory>;
}

const initialState: State = {
  history: {},
};

export const location = createSlice({
  name: "location",
  initialState,
  reducers: {
    clearHistory(state) {
      state.history = initialState.history;
    },
    clearTractorHistory(
      state,
      action: PayloadAction<{
        serial: string;
      }>
    ) {
      const { serial } = action.payload;
      state.history[serial] = { historical: [], recent: [] };
    },
    updateHistory(
      state,
      action: PayloadAction<{
        serial: string;
        updates: LocationHistoryRecord[];
      }>
    ) {
      const { serial, updates } = action.payload;
      // Read the non-draft arrays so Immer doesn't proxy every record.
      const previous = original(state)?.history[serial] ?? EMPTY_HISTORY;
      const last = previous.recent.at(-1) ?? previous.historical.at(-1);
      if (!extendsHistory(last?.timestamp, updates)) {
        state.history[serial] = {
          historical: merge([
            ...previous.historical,
            ...previous.recent,
            ...updates,
          ]),
          recent: [],
        };
        return;
      }
      const recent = [...previous.recent, ...updates];
      state.history[serial] =
        recent.length < RECENT_LIMIT
          ? { historical: previous.historical, recent }
          : { historical: [...previous.historical, ...recent], recent: [] };
    },
  },
});

// Whether `updates` are strictly increasing and all later than `last`.
const extendsHistory = (
  last: string | undefined,
  updates: LocationHistoryRecord[]
): boolean => {
  let previous = last;
  for (const { timestamp } of updates) {
    if (!previous || !timestamp || timestamp <= previous) {
      return false;
    }
    previous = timestamp;
  }
  return true;
};

// Sorts by timestamp and drops consecutive duplicates.
const merge = (records: LocationHistoryRecord[]): LocationHistoryRecord[] => {
  records.sort(Comparators.comparing((record) => record.timestamp));
  return records.filter((record, index) => {
    if (index === 0) {
      return true;
    }
    const previous = records[index - 1];
    return (
      record.timestamp &&
      previous?.timestamp &&
      record.timestamp > previous.timestamp
    );
  });
};

export const { updateHistory, clearHistory, clearTractorHistory } =
  location.actions;
